import type {
  EpisodeVisitVitalsReading,
  EpisodeVitalsOrderedParameters,
  VisitVitalsValues,
  VitalParameterRange,
} from '../../services/api/types.gen';

export type VitalKey = keyof VisitVitalsValues;

/** Client-side min/max/latest for the visible (filtered) readings. */
export type VitalSummaryStat = {
  min: number | null;
  max: number | null;
  latest: number | null;
  latest_display: string | null;
  latest_visit_id: string | null;
  latest_visit_date: string | null;
  unit: string;
  count: number;
};

export type EpisodeVitalsSummaryStats = Record<VitalKey, VitalSummaryStat>;

export type VitalColumn = {
  key: VitalKey;
  label: string;
  /** Include in the trend-chart grid (BP sys/dia share one chart). */
  chart: boolean;
};

/** Columns for the summary panel and per-visit table (Kinnser-inspired). */
export const VITAL_COLUMNS: VitalColumn[] = [
  { key: 'bp_sys', label: 'BP Sys', chart: true },
  { key: 'bp_dia', label: 'BP Dia', chart: true },
  { key: 'hr', label: 'HR', chart: true },
  { key: 'resp', label: 'Resp', chart: true },
  { key: 'temp', label: 'Temp', chart: true },
  { key: 'spo2', label: 'SpO₂', chart: true },
  { key: 'weight', label: 'Weight', chart: true },
  { key: 'blood_sugar', label: 'BS', chart: false },
  { key: 'pain', label: 'Pain', chart: false },
];

/** Keys with `chart: true` on VITAL_COLUMNS (single source of truth). */
export type ChartVitalKey = Extract<
  VitalKey,
  'bp_sys' | 'bp_dia' | 'hr' | 'resp' | 'temp' | 'spo2' | 'weight'
>;

export const CHART_SERIES_KEYS: ChartVitalKey[] = VITAL_COLUMNS.filter(
  (c): c is VitalColumn & { key: ChartVitalKey; chart: true } => c.chart
).map(c => c.key);

/** Chart section keys; bp_dia is drawn with bp_sys, not as its own panel. */
export const CHART_VITAL_KEYS: ChartVitalKey[] = CHART_SERIES_KEYS.filter(
  k => k !== 'bp_dia'
);

/**
 * Minimum Y-span (and optional hard bounds) so small physiologic deltas do not
 * fill the chart and look dramatic.
 */
export const CHART_Y_SCALE: Record<
  ChartVitalKey,
  { minSpan: number; floor?: number; ceil?: number }
> = {
  bp_sys: { minSpan: 40, floor: 60, ceil: 220 },
  bp_dia: { minSpan: 30, floor: 30, ceil: 140 },
  hr: { minSpan: 40, floor: 30, ceil: 180 },
  resp: { minSpan: 12, floor: 6, ceil: 40 },
  temp: { minSpan: 4, floor: 95, ceil: 105 },
  spo2: { minSpan: 15, floor: 80, ceil: 100 },
  weight: { minSpan: 20, floor: 50, ceil: 500 },
};

const VITAL_UNITS: Record<VitalKey, string> = {
  bp_sys: 'mmHg',
  bp_dia: 'mmHg',
  hr: 'bpm',
  resp: '/min',
  temp: '°F',
  spo2: '%',
  weight: 'lbs',
  blood_sugar: 'mg/dL',
  pain: '',
};

export function emptyVitalStat(unit = ''): VitalSummaryStat {
  return {
    min: null,
    max: null,
    latest: null,
    latest_display: null,
    latest_visit_id: null,
    latest_visit_date: null,
    unit,
    count: 0,
  };
}

export function getStat(
  summary: EpisodeVitalsSummaryStats | undefined,
  key: VitalKey
): VitalSummaryStat {
  return summary?.[key] ?? emptyVitalStat(VITAL_UNITS[key]);
}

/**
 * Min/max/latest for the currently visible readings (respects date/discipline
 * filters). Readings should already be the filtered set; order does not matter.
 */
export function summarizeReadings(
  readings: EpisodeVisitVitalsReading[]
): EpisodeVitalsSummaryStats {
  const sorted = [...readings].sort((a, b) => {
    const byDate = a.visit_date.localeCompare(b.visit_date);
    if (byDate !== 0) return byDate;
    const aSec = a.visit_start_seconds ?? -1;
    const bSec = b.visit_start_seconds ?? -1;
    if (aSec !== bSec) return aSec - bSec;
    return a.visit_id.localeCompare(b.visit_id);
  });

  const summary = {} as EpisodeVitalsSummaryStats;
  for (const col of VITAL_COLUMNS) {
    const values: number[] = [];
    let latest: number | null = null;
    let latestDisplay: string | null = null;
    let latestVisitId: string | null = null;
    let latestVisitDate: string | null = null;
    for (const reading of sorted) {
      const vital = reading.vitals[col.key];
      if (vital?.value == null || Number.isNaN(vital.value)) continue;
      values.push(vital.value);
      latest = vital.value;
      latestDisplay = vital.display ?? null;
      latestVisitId = reading.visit_id;
      latestVisitDate = reading.visit_date;
    }
    summary[col.key] = {
      min: values.length ? Math.min(...values) : null,
      max: values.length ? Math.max(...values) : null,
      latest,
      latest_display: latestDisplay,
      latest_visit_id: latestVisitId,
      latest_visit_date: latestVisitDate,
      unit: VITAL_UNITS[col.key],
      count: values.length,
    };
  }
  return summary;
}

/**
 * Ordered-parameter range for a vital. Blood sugar uses exact RN condition
 * labels (`Fasting` → fasting VSP, `Non-Fasting` → random VSP); other/unknown
 * conditions (e.g. `2 Hr PP`) skip highlighting.
 */
export function getOrderedRange(
  params: EpisodeVitalsOrderedParameters | undefined,
  key: VitalKey,
  context?: string | null
): VitalParameterRange | null {
  if (!params || key === 'pain') return null;
  if (key === 'blood_sugar') {
    const condition = (context ?? '').trim().toLowerCase();
    if (condition === 'fasting') return params.blood_sugar ?? null;
    if (condition === 'non-fasting') return params.blood_sugar_random ?? null;
    return null;
  }
  return params[key] ?? null;
}

export function formatVitalNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** True when a numeric reading falls outside ordered low/high bounds. */
export function isOutsideOrderedRange(
  value: number | null | undefined,
  range: VitalParameterRange | null | undefined
): boolean {
  if (value == null || Number.isNaN(value) || !range) return false;
  if (range.low != null && value < range.low) return true;
  if (range.high != null && value > range.high) return true;
  return false;
}

/**
 * Build a calm Y domain: enforce a minimum span around the data, then clamp to
 * physiologic floor/ceil when present.
 */
export function yDomainForValues(
  values: number[],
  key: ChartVitalKey
): [number, number] | ['auto', 'auto'] {
  if (values.length === 0) return ['auto', 'auto'];
  const scale = CHART_Y_SCALE[key];
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  let min = dataMin;
  let max = dataMax;
  const span = max - min;
  if (span < scale.minSpan) {
    const pad = (scale.minSpan - span) / 2;
    min -= pad;
    max += pad;
  } else {
    const pad = span * 0.1;
    min -= pad;
    max += pad;
  }
  if (scale.floor != null) min = Math.max(min, scale.floor);
  if (scale.ceil != null) max = Math.min(max, scale.ceil);
  // If clamping collapsed the domain (e.g. all values near a bound), re-expand
  // within the physiologic window.
  if (max - min < scale.minSpan) {
    const mid = (dataMin + dataMax) / 2;
    min = mid - scale.minSpan / 2;
    max = mid + scale.minSpan / 2;
    if (scale.floor != null) min = Math.max(min, scale.floor);
    if (scale.ceil != null) max = Math.min(max, scale.ceil);
  }
  if (max <= min) {
    max = min + scale.minSpan;
  }
  return [Number(min.toFixed(2)), Number(max.toFixed(2))];
}
